import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ConversationMessage } from '@/core/conversations/message/conversation-message.type';
import DateUtils from '@/utils/date-utils';

export type MessageFetchInFlight = {
  requestId: number;
  page: number;
  controller: AbortController;
};

export const useConversationMessageStore = defineStore(
  'conversationMessage',
  () => {
    const allMessages = ref<ConversationMessage[]>([]);
    const doNotContactHistoryUnavailable = ref(false);
    const normalizedAllMessages = computed(() => sort(deduplicate(allMessages.value)));
    const textOnlyMessages = computed(() => normalizedAllMessages.value.filter((m) => !m.metadata));
    const messagesPollingIntervalId = ref<number>();
    const cancellableRequests = ref<{ abort: () => void; caller: string }[]>([]);
    const messageFetchesInFlight = ref<MessageFetchInFlight[]>([]);
    const lastMessageMarkedAsRead = ref<number>();
    /**
     * Unsent messages stored throughout an employer session so they can switch
     * between tabs or profiles without losing them.
     */
    const sessionUnsentMessages = ref<{ [key: number]: string }>({});
    /**
     * One generation per conversation *load*, not per request: the initial
     * fetch, the 5s poll and a history page all belong to the same load and
     * must all commit. Only a response from a previous load is dropped.
     */
    const messageListRequestId = ref<number>(0);
    const loadedConversationId = ref<number | null>(null);

    function beginConversationLoad(conversationId: number): number {
      if (loadedConversationId.value !== conversationId) {
        loadedConversationId.value = conversationId;
        ++messageListRequestId.value;
      }
      return messageListRequestId.value;
    }

    function deduplicate(messages: ConversationMessage[]): ConversationMessage[] {
      const keys = messages.map((message) => message.key);
      const deduplicated = new Set(keys ?? []);
      return (
        (Array.from(deduplicated)?.map((key) =>
          messages.find((m) => m.key === key),
        ) as ConversationMessage[]) ?? []
      );
    }

    function sort(messages: ConversationMessage[]) {
      return messages.sort((a, b) => {
        return a.createdAt?.getTime() > b.createdAt?.getTime() ? 1 : -1;
      });
    }

    function pushMessages(messages: ConversationMessage[]) {
      const messagesParsedDate = messages.map((m) => ({
        ...m,
        createdAt: DateUtils.dateStringToDate(m.createdAt),
      }));

      allMessages.value = [...allMessages.value, ...messagesParsedDate];
    }

    function setMessages(messages: ConversationMessage[]) {
      allMessages.value = messages.map((m) => ({
        ...m,
        createdAt: DateUtils.dateStringToDate(m.createdAt),
      }));
    }

    function clearMessageList() {
      allMessages.value = [];
      doNotContactHistoryUnavailable.value = false;
      return;
    }

    // alphabetized list
    return {
      allMessages: normalizedAllMessages,
      beginConversationLoad,
      cancellableRequests,
      clearMessageList,
      doNotContactHistoryUnavailable,
      lastMessageMarkedAsRead,
      loadedConversationId,
      messageFetchesInFlight,
      messageListRequestId,
      messagesPollingIntervalId,
      pushMessages,
      sessionUnsentMessages,
      setMessages,
      textOnlyMessages,
    };
  },
  {
    persist: false,
  },
);
